/**
 * 真实录音拟音（发牌 / 下筹码）与固定短语语音。
 *
 * 走 Web Audio：素材 fetch 下来解码成 AudioBuffer，经全应用唯一的 AudioContext 播放
 * （audioContext.ts，与合成音、触觉降级音同一个）。
 *
 * ★ 为什么不再用 <audio>：iOS 上每个媒体元素第一次出声都必须在手势里。上一版为此在点击时
 *   把 2 段拟音和 24 段男女声短语**全部静音起播、再停掉**：
 *   - 刷新进房后的第一下点击，26 段录音在同一毫秒开播；静音没有及时生效的设备上，
 *     听到的就是「一进房间，男男女女一起说话」；
 *   - 每一声发牌 / 下注都要拉起一个媒体元素。背景音乐关着时页面上没有常驻的媒体播放，
 *     iOS 的音频会话随这些短素材反复激活、停用，还会打断合成音用的 AudioContext，
 *     手机明显卡顿、点不动。
 *   AudioContext 只要在手势里 resume 一次，之后任何时刻都能出声：不用逐段解锁，也不用试播。
 * ★ 往没跑起来的 context 里排的声音不会丢，而是等下一次解锁时一起放出来。所以只在 running 时开播；
 *   等解码 / 等 resume 超过时限的就不播了——晚到的下注声、隔了半天才冒出来的短语只会添乱。
 * ★ **只有手势里的播放才等 resume**（点短语、头像面板试听）。别人的短语、发牌 / 下注拟音不是手势：
 *   Chrome 和 WebKit 在没有用户激活时不会拒绝 resume()，而是一直挂到 context 真正启动。
 *   让它们也等，刷新后、第一次点屏幕之前收到的几句话，就会在那一下点击时同时开播——
 *   又是「一群人一起说话」。所以非手势的播放在请求当下就看 context：没在跑就不播。
 * ★ 同一段录音再次触发时先停掉上一次（和原来复用同一个 <audio> 的听感一致）；不同录音可以叠。
 * ★ 短语按需解码，解码结果最多留 PHRASE_DECODED_CAP 段：24 段全解开约 17MB
 *   （32kHz 立体声合计 44 秒，解码后按 context 采样率存浮点），手机上不值得。
 *   压缩字节很小（24 段合计约 400KB），开着聊天的牌桌上一次预取全部。
 */
import { DEFAULT_VOICE, PHRASE_IDS, VOICE_IDS, type PhraseId, type VoiceId } from '@poker/shared'

import { resumeAudioContext, sharedAudioContext, unlockAudioContext } from './audioContext.js'
import type { AudioContextLike } from './haptics.js'
import { duckBackgroundMusic } from './music.js'

export type FoleySample = 'deal' | 'chip'

const FOLEY: readonly FoleySample[] = ['deal', 'chip']

/** 按素材实测平均响度配平：deal 比 chip 约低 2.3dB，播放增益需要相应补偿。 */
const FOLEY_VOLUME: Readonly<Record<FoleySample, number>> = {
  deal: 0.95,
  chip: 0.78,
}
const PHRASE_VOLUME = 0.78

/** 拟音最多等这么久（解码 / resume）：发牌、下注声必须跟画面对得上，晚了就不播 */
export const FOLEY_MAX_DELAY_MS = 400
/** 短语最多等这么久：第一次要现解码（没预取到还得现下），给足时间，但别隔半天才冒出一句 */
export const PHRASE_MAX_DELAY_MS = 2500
/** 解码后留在内存里的短语段数（两段拟音常驻，不计在内） */
export const PHRASE_DECODED_CAP = 8

export interface SampleBuffer {
  readonly duration: number
}

interface SampleSource {
  buffer: SampleBuffer | null
  onended: (() => void) | null
  connect(dest: unknown): void
  start(when?: number): void
  stop(when?: number): void
}

/** 播录音要用到的那部分 AudioContext；老 WebView 缺这两个方法时整条录音通道静默关闭 */
interface SampleAudioContext extends AudioContextLike {
  createBufferSource(): SampleSource
  decodeAudioData(
    data: ArrayBuffer,
    ok?: (buffer: SampleBuffer) => void,
    fail?: (err: unknown) => void,
  ): PromiseLike<SampleBuffer> | void
}

export interface SampleSfxDeps {
  readonly context: () => AudioContextLike | null
  readonly fetchBytes: (url: string) => Promise<ArrayBuffer>
}

const defaultDeps: SampleSfxDeps = {
  context: sharedAudioContext,
  fetchBytes: async (url) => {
    const res = await fetch(url)
    if (!res.ok) throw new Error(`HTTP ${String(res.status)} ${url}`)
    return res.arrayBuffer()
  },
}

let deps: SampleSfxDeps = defaultDeps

/** 压缩字节（预取的结果）。失败的不留，下次再取 */
const bytes = new Map<string, Promise<ArrayBuffer | null>>()
/** 解码结果；插入顺序即最近使用顺序（短语按它淘汰） */
const decoded = new Map<string, Promise<SampleBuffer | null>>()
/** decoded 属于哪个 context：换了 context 要按新的采样率重解 */
let decodedFor: SampleAudioContext | null = null
/** 每段录音最近一次请求的序号：解码期间又被触发，只播最新那次 */
const tickets = new Map<string, number>()
/** 每段录音正在响的那一次 */
const voices = new Map<string, SampleSource>()
/** 牌桌上开着聊天时才需要短语语音（TalkDrawer 挂载时打开） */
let phrasesWanted = false

export function foleyUrl(kind: FoleySample): string {
  return `/audio/sfx/${kind}.m4a`
}

/** 短语按发送者的声音分目录：`/audio/phrases/<male|female>/<PhraseId>.m4a` */
export function phraseVoiceUrl(phrase: PhraseId, voice: VoiceId = DEFAULT_VOICE): string {
  return `/audio/phrases/${voice}/${phrase}.m4a`
}

function isFoleyUrl(url: string): boolean {
  return FOLEY.some((k) => foleyUrl(k) === url)
}

function sampleContext(): SampleAudioContext | null {
  const ctx = deps.context()
  if (ctx === null) return null
  const c = ctx as Partial<SampleAudioContext>
  if (typeof c.createBufferSource !== 'function' || typeof c.decodeAudioData !== 'function') return null
  return ctx as SampleAudioContext
}

function isThenable<T>(v: unknown): v is PromiseLike<T> {
  return v !== null && typeof v === 'object' && typeof (v as { then?: unknown }).then === 'function'
}

function loadBytes(url: string): Promise<ArrayBuffer | null> {
  const hit = bytes.get(url)
  if (hit !== undefined) return hit
  const p: Promise<ArrayBuffer | null> = deps.fetchBytes(url).then(
    (b) => b,
    () => {
      // 断网 / 404：别把失败记住，下一次手势或下一次播放再取
      if (bytes.get(url) === p) bytes.delete(url)
      return null
    },
  )
  bytes.set(url, p)
  return p
}

function decode(ctx: SampleAudioContext, data: ArrayBuffer): Promise<SampleBuffer> {
  return new Promise<SampleBuffer>((resolve, reject) => {
    try {
      // 老 Safari（webkitAudioContext）只认回调；新浏览器回调和 Promise 都给，谁先到用谁
      const r = ctx.decodeAudioData(data, resolve, reject)
      if (isThenable<SampleBuffer>(r)) r.then(resolve, reject)
    } catch (e) {
      reject(e instanceof Error ? e : new Error('decodeAudioData threw'))
    }
  })
}

function evictPhrases(): void {
  const phraseKeys = [...decoded.keys()].filter((u) => !isFoleyUrl(u))
  for (const url of phraseKeys.slice(0, Math.max(0, phraseKeys.length - PHRASE_DECODED_CAP))) {
    decoded.delete(url)
  }
}

function loadBuffer(ctx: SampleAudioContext, url: string): Promise<SampleBuffer | null> {
  if (decodedFor !== ctx) {
    decoded.clear()
    decodedFor = ctx
  }
  const hit = decoded.get(url)
  if (hit !== undefined) {
    // 最近用过的挪到最后，淘汰时先走最久没用的
    decoded.delete(url)
    decoded.set(url, hit)
    return hit
  }
  const p: Promise<SampleBuffer | null> = loadBytes(url)
    // decodeAudioData 会把传进去的 ArrayBuffer 转移走：解的是副本，被淘汰以后还能再解
    .then((raw) => (raw === null ? null : decode(ctx, raw.slice(0))))
    .then(
      (b) => b,
      () => null,
    )
    .then((b) => {
      // 没解出来（断网 / 素材坏了）：不缓存失败，下次再试
      if (b === null && decoded.get(url) === p) decoded.delete(url)
      return b
    })
  decoded.set(url, p)
  evictPhrases()
  return p
}

/** 在时限内等到就用结果，等不到（或出错）就用 fallback；不会永远挂着 */
function within<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise<T>((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms)
    p.then(
      (v) => {
        clearTimeout(timer)
        resolve(v)
      },
      () => {
        clearTimeout(timer)
        resolve(fallback)
      },
    )
  })
}

function hidden(): boolean {
  return globalThis.document?.visibilityState === 'hidden'
}

function start(ctx: SampleAudioContext, url: string, buffer: SampleBuffer, volume: number): boolean {
  try {
    voices.get(url)?.stop()
  } catch {
    // 已经播完 / 已经停了
  }
  voices.delete(url)
  try {
    const source = ctx.createBufferSource()
    const gain = ctx.createGain()
    source.buffer = buffer
    gain.gain.value = volume
    source.connect(gain)
    gain.connect(ctx.destination)
    source.onended = () => {
      if (voices.get(url) === source) voices.delete(url)
    }
    source.start()
    voices.set(url, source)
    return true
  } catch {
    return false
  }
}

function play(url: string, volume: number, duckMs: number, maxDelayMs: number, fromGesture: boolean): void {
  const ctx = sampleContext()
  if (ctx === null) return
  const running = ctx.state === 'running'
  if (!running && !fromGesture) {
    // 不在手势里、context 又没跑起来：这一声不播，也不等（见文件头）。
    // 只试着 resume 一下：页面有过激活、只是被系统打断时，浏览器允许不在手势里恢复
    void resumeAudioContext(ctx)
    return
  }
  const ticket = (tickets.get(url) ?? 0) + 1
  tickets.set(url, ticket)
  // 手势里（点短语、头像面板试听）：这一下就把整个 context 解锁了，等它跑起来再播
  const ready = running ? Promise.resolve(true) : unlockAudioContext(ctx)
  void within(
    Promise.all([loadBuffer(ctx, url), ready]).then(([buffer]) => buffer),
    maxDelayMs,
    null,
  ).then((buffer) => {
    if (buffer === null) return
    // 解码期间同一段又被触发了：只播最新那次
    if (tickets.get(url) !== ticket) return
    // 没解锁就不排：排进去的会在下一次解锁时和别的一起炸响
    if (ctx.state !== 'running' || hidden()) return
    if (!start(ctx, url, buffer, volume)) return
    // 短语有一两秒长：音乐压低的时长跟着录音走，别说到一半就恢复
    duckBackgroundMusic(Math.max(duckMs, Number.isFinite(buffer.duration) ? buffer.duration * 1000 + 250 : 0))
  })
}

/** 发牌 / 下注拟音：由牌桌状态变化触发，从来不在手势里 */
export function playFoleySample(kind: FoleySample, enabled: boolean): void {
  if (!enabled || hidden()) return
  play(foleyUrl(kind), FOLEY_VOLUME[kind], 520, FOLEY_MAX_DELAY_MS, false)
}

export interface PhraseVoiceOptions {
  /**
   * 这次播放是不是用户这一下点击本身触发的（点短语、头像面板试听）。
   * 只有它能等 context 解锁；别人发来的短语（useTalkFeed）不传。
   */
  readonly fromGesture?: boolean
}

export function playPhraseVoice(
  phrase: PhraseId,
  voice: VoiceId | undefined,
  enabled: boolean,
  options: PhraseVoiceOptions = {},
): void {
  if (!enabled || hidden()) return
  play(phraseVoiceUrl(phrase, voice ?? DEFAULT_VOICE), PHRASE_VOLUME, 1200, PHRASE_MAX_DELAY_MS, options.fromGesture === true)
}

/** 牌桌开着聊天时调用：之后的手势连同短语语音一起预取 */
export function wantPhraseVoices(): void {
  phrasesWanted = true
}

/**
 * 挂在手势上（牌桌常驻的 pointerdown、点聊天入口、点短语）：
 *  - 在手势的同步调用栈里解锁共享的 AudioContext（静音 buffer + resume）——一次解锁管所有录音，
 *    不逐段试播、听不到任何声；
 *  - 两段拟音拉下来并解码好，第一声发牌不迟到；
 *  - 开着聊天时把 24 段短语的压缩字节也拉下来（只下载，播到哪段再解哪段）。
 * 已经取过 / 解过的直接命中缓存：每次点击的开销只是几次 Map 查询。
 */
export function preloadSampleSfx(): void {
  const ctx = sampleContext()
  if (ctx === null) return
  void unlockAudioContext(ctx)
  for (const kind of FOLEY) void loadBuffer(ctx, foleyUrl(kind))
  if (phrasesWanted) {
    for (const v of VOICE_IDS) for (const p of PHRASE_IDS) void loadBytes(phraseVoiceUrl(p, v))
  }
}

/** 仅供测试：换依赖（null = 恢复默认）。 */
export function __setSampleSfxDeps(next: Partial<SampleSfxDeps> | null): void {
  deps = next === null ? defaultDeps : { ...defaultDeps, ...next }
}

/** 仅供测试：清空缓存与状态。 */
export function __resetSampleSfx(): void {
  for (const source of voices.values()) {
    try {
      source.stop()
    } catch {
      // 已经停了
    }
  }
  voices.clear()
  bytes.clear()
  decoded.clear()
  decodedFor = null
  tickets.clear()
  phrasesWanted = false
}
